import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../utils/api';
import TerminalWindow from '../components/TerminalWindow';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import remarkGfm from 'remark-gfm';

// Utility to render terminal-style headers
function renderTerminalText(text) {
  return text.split('\n').map((line, idx) => {
    const trimmed = line.trimStart();
    if (trimmed.startsWith('#')) {
      // Find the # and the rest
      const match = trimmed.match(/^(#+)(\s*)(.*)$/);
      if (match) {
        return (
          <div key={idx} style={{ color: '#00ff66', fontSize: '1.3em', fontWeight: 700, margin: '0.2em 0' }}>
            <span style={{ fontWeight: 900, marginRight: '0.3em' }}>{match[1]}</span>{match[2]}{match[3]}
          </div>
        );
      }
    }
    // Handle empty lines by adding spacing
    if (line.trim() === '') {
      return <div key={idx} style={{ height: '1em', margin: '0.5em 0' }}></div>;
    }
    return <div key={idx} style={{ margin: '0.2em 0' }}>{line}</div>;
  });
}

const markdownComponents = {
  h1: ({node, ...props}) => (
    <div style={{ color: '#00ff66', fontSize: '1.3em', fontWeight: 700, margin: '0.2em 0' }}>
      <span style={{ fontWeight: 900, marginRight: '0.3em' }}>#</span>
      {props.children}
    </div>
  ),
  h2: ({node, ...props}) => (
    <div style={{ color: '#00ff66', fontSize: '1.15em', fontWeight: 700, margin: '0.2em 0' }}>
      <span style={{ fontWeight: 900, marginRight: '0.3em' }}>##</span>
      {props.children}
    </div>
  ),
  h3: ({node, ...props}) => (
    <div style={{ color: '#00ff66', fontSize: '1.05em', fontWeight: 700, margin: '0.2em 0' }}>
      <span style={{ fontWeight: 900, marginRight: '0.3em' }}>###</span>
      {props.children}
    </div>
  ),
  h4: ({node, ...props}) => (
    <div style={{ color: '#00ff66', fontSize: '1em', fontWeight: 700, margin: '0.2em 0' }}>
      <span style={{ fontWeight: 900, marginRight: '0.3em' }}>####</span>
      {props.children}
    </div>
  ),
  h5: ({node, ...props}) => (
    <div style={{ color: '#00ff66', fontSize: '0.95em', fontWeight: 700, margin: '0.2em 0' }}>
      <span style={{ fontWeight: 900, marginRight: '0.3em' }}>#####</span>
      {props.children}
    </div>
  ),
  h6: ({node, ...props}) => (
    <div style={{ color: '#00ff66', fontSize: '0.9em', fontWeight: 700, margin: '0.2em 0' }}>
      <span style={{ fontWeight: 900, marginRight: '0.3em' }}>######</span>
      {props.children}
    </div>
  ),
  code: ({node, inline, className, children, ...props}) => {
    const match = /language-(\w+)/.exec(className || '');
    return !inline && match ? (
      <div className="my-4">
        <SyntaxHighlighter
          style={oneDark}
          language={match[1]}
          PreTag="div"
          customStyle={{
            margin: 0,
            borderRadius: '8px',
            fontSize: '14px',
            lineHeight: '1.5',
            fontFamily: 'JetBrains Mono, Consolas, Monaco, monospace',
          }}
          {...props}
        >
          {String(children).replace(/\n$/, '')}
        </SyntaxHighlighter>
      </div>
    ) : (
      <code 
        className={className} 
        style={{
          backgroundColor: '#2d3748',
          color: '#e2e8f0',
          padding: '2px 6px',
          borderRadius: '4px',
          fontSize: '0.9em',
          fontFamily: 'JetBrains Mono, Consolas, Monaco, monospace',
        }}
        {...props}
      >
        {children}
      </code>
    );
  },
  pre: ({node, children, ...props}) => (
    <div className="my-4">
      <pre
        style={{
          backgroundColor: '#282c34',
          color: '#abb2bf',
          padding: '16px',
          borderRadius: '8px',
          fontSize: '14px',
          lineHeight: '1.5',
          fontFamily: 'JetBrains Mono, Consolas, Monaco, monospace',
          overflow: 'auto',
          border: '1px solid #3e4451',
        }}
        {...props}
      >
        {children}
      </pre>
    </div>
  ),
};

const PostDetail = () => {
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [command, setCommand] = useState('');
  const [output, setOutput] = useState(null);
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const { slug } = useParams();

  useEffect(() => {
    const fetchPost = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await api.get(`/posts/${slug}/`);
        setPost(response.data);
      } catch (err) {
        setError('Failed to fetch post');
        setPost(null);
      } finally {
        setLoading(false);
      }
    };
    fetchPost();
  }, [slug]);

  useEffect(() => {
    inputRef.current && inputRef.current.focus();
  }, [post]);

  const handleCommand = (cmd) => {
    if (!post) return;
    const trimmed = cmd.trim();
    if (trimmed === 'help') {
      setOutput(
        <div className="mt-4 text-[#cdd6f4]">
          <div>Available commands:</div>
          <ul className="list-disc pl-6">
            <li><span className="text-[#a6e3a1]">help</span>: Show this help message</li>
            <li><span className="text-[#a6e3a1]">cat &lt;id&gt;</span>: Show the post content</li>
            <li><span className="text-[#a6e3a1]">:q</span>: Exit vim (go back to blog)</li>
            <li><span className="text-[#a6e3a1]">hist</span>: Show history/help for exiting vim</li>
          </ul>
        </div>
      );
    } else if (trimmed.startsWith('cat')) {
      const parts = trimmed.split(/\s+/);
      if (parts.length !== 2 || !/^[0-9]+$/.test(parts[1])) {
        setOutput(<div className="mt-4 text-[#f38ba8]">usage: cat &lt;id&gt;</div>);
        return;
      }
      const id = parts[1];
      if (post && String(post.id) === id) {
        setOutput(
          <div className="mt-4">
            <div className="flex flex-col gap-2 mb-2">
              <span className="text-2xl font-bold text-[#e5e5e5]">{post.title}</span>
              <span className="text-xs text-[#39c5bb]">{post.published_at || post.date}</span>
            </div>
            <div className="mb-2">
              <span className="text-[#39c5bb]">views:</span> <span className="text-[#00ff66]">{post.views}</span> | <span className="text-[#39c5bb]">comments:</span> <span className="text-[#00ff66]">{post.comments_count}</span> | <span className="text-[#39c5bb]">likes:</span> <span className="text-[#00ff66]">{post.likes}</span>
            </div>
            <div className="pl-2 text-[#e5e5e5] bg-[#181a1b] rounded p-4 text-base prose prose-invert max-w-none">
              <ReactMarkdown 
                components={markdownComponents}
                remarkPlugins={[remarkGfm]}
              >
                {post.content}
              </ReactMarkdown>
            </div>
          </div>
        );
      } else {
        setOutput(<div className="mt-4 text-[#f38ba8]">No such post with ID: {id}</div>);
      }
    } else if (trimmed === ':q') {
      setOutput(<div className="mt-4 text-[#00ff66]">Exiting vim...</div>);
      setTimeout(() => navigate('/blog'), 600);
    } else if (trimmed === 'hist') {
      setOutput(
        <div className="mt-4 text-[#cdd6f4]">
          <div>To exit vim, type <span className="bg-[#23272e] px-1 py-0.5 rounded text-[#e5e5e5]">:q</span></div>
        </div>
      );
    } else {
      setOutput(<div className="mt-4 text-[#ff5555]">command not found: {cmd}</div>);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    handleCommand(command);
    setCommand('');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#181a1b] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4 font-mono">
          <div className="flex items-center gap-2">
            <svg className="animate-spin h-6 w-6 text-[#00ff66]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" strokeOpacity="0.2" /><path d="M12 2a10 10 0 0 1 10 10" /></svg>
            <span className="text-[#00ff66] text-lg">abdelhadi@blog:~$</span>
            <span className="text-[#e5e5e5] ml-2">Loading post...</span>
          </div>
          <div className="text-[#39c5bb] text-sm mt-2">Please wait while we fetch the post details.</div>
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#23272e] via-[#181a1b] to-[#23272e] flex items-center justify-center">
        <div className="text-center">
          <div className="p-8 bg-gradient-to-r from-[#23272e] to-[#181a1b] rounded-3xl inline-block mb-6">
            <span className="text-2xl text-[#ff5555]">!</span>
          </div>
          <h3 className="text-2xl font-semibold text-gray-100 mb-4">Error Loading Post</h3>
          <p className="text-gray-400 mb-8 max-w-md mx-auto">
            {error || 'Post not found.'}
          </p>
          <button 
            onClick={() => navigate(-1)}
            className="flex items-center px-6 py-3 bg-gradient-to-r from-[#39c5bb] to-[#00ff66] text-white rounded-2xl hover:from-[#00ff66] hover:to-[#39c5bb] transition-all duration-300 font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-1 mx-auto"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full flex flex-col items-center justify-start py-8 sm:py-16 px-2 sm:px-4 md:px-8 bg-[#181a1b]" dir="ltr">
      <TerminalWindow 
        prompt="abdelhadi@blog:~$" 
        className="w-full max-w-4xl"
        isInteractive={true}
        command={command}
        onCommandChange={e => setCommand(e.target.value)}
        onCommandSubmit={handleSubmit}
        commandInputRef={inputRef}
        placeholder="Type a command (hint: :q to exit vim)"
      >
        {/* Terminal content */}
        <div className="flex flex-col gap-8 font-mono animate-fadein text-left min-h-[400px] overflow-x-auto">
          {/* Welcome message */}
          <div className="text-[#00ff66] text-base mb-2">
            # Welcome to <span className="text-[#39c5bb] font-semibold">abdelhadi@blog</span> — try <span className="bg-[#23272e] px-2 py-1 rounded text-[#e5e5e5]">cat {post.id}</span> or <span className="bg-[#23272e] px-2 py-1 rounded text-[#e5e5e5]">:q</span>
          </div>
          <div className="text-[#39c5bb] text-sm mb-2">hint: exit vim to go back</div>
          {/* Output from prompt */}
          {output}
          {/* Default post content */}
          {!output && (
            <div className="mt-4">
              <div className="flex flex-col gap-2 mb-2">
                <span className="text-2xl font-bold text-[#e5e5e5]">{post.title}</span>
                <span className="text-xs text-[#39c5bb]">{post.published_at || post.date}</span>
              </div>
              <div className="mb-2">
                <span className="text-[#39c5bb]">views:</span> <span className="text-[#00ff66]">{post.views}</span> | <span className="text-[#39c5bb]">comments:</span> <span className="text-[#00ff66]">{post.comments_count}</span> | <span className="text-[#39c5bb]">likes:</span> <span className="text-[#00ff66]">{post.likes}</span>
              </div>
              <div className="pl-2 text-[#e5e5e5] bg-[#181a1b] rounded p-4 text-base prose prose-invert max-w-none">
                <ReactMarkdown 
                  components={markdownComponents}
                  remarkPlugins={[remarkGfm]}
                >
                  {post.content}
                </ReactMarkdown>
              </div>
            </div>
          )}
        </div>
      </TerminalWindow>
      {/* Subtle background pattern */}
      <div className="pointer-events-none fixed inset-0 z-0 opacity-10" style={{background: 'radial-gradient(circle at 60% 40%, #00ff66 0%, transparent 60%)'}} />
      <style>{`
        @keyframes fadein { from { opacity: 0; transform: translateY(16px);} to { opacity: 1; transform: none; } }
        .animate-fadein { animation: fadein 0.7s cubic-bezier(.4,1.7,.6,.97) both; }
      `}</style>
    </div>
  );
};

export default PostDetail; 